/**
 * Report templates store a clinic's choices: which fields, tags, questionnaire
 * elements and text sections, in which order, plus letterhead and cover.
 * Patient, CPR, journal, marking and questionnaire content is resolved live
 * for preview and PDF and is never written back onto the template or report.
 */

import { formatResponse, topicRowParts } from '@/lib/formsAnswerValue';
import type { ScoringConfig } from '@/lib/formsScoring';
import { formatRevealedCpr } from '@/lib/cprDisplay';
import { shownQuestionnaireScore } from '@/lib/questionnaireScoreDisplay';
import type { FormsQuestion } from '@/hooks/useFormsAssignmentResponses';
import { chartHasFigure, type ChartFigure } from '@/lib/patientReports/chartFigure';
import {
  historyChartForTemplate,
  questionnaireCharts,
  selectQuestionnaireCharts,
  withHistoryChart,
} from '@/lib/patientReports/questionnaireCharts';
import {
  newReportBlockId,
  type HighlightTagSource,
  type JournalNoteSource,
  type MarkingSource,
  type PatientCardSource,
  type PatientReportBlock,
  type PatientReportCatalog,
  type PatientReportLetterheadChoice,
  type PatientReportLetterheadMode,
  type PatientReportSourceRef,
  type QuestionnaireSource,
} from '@/lib/patientReports/model';

export const PATIENT_FIELD_KEYS = [
  'name',
  'cpr',
  'contact',
  'address',
  'job_title',
  'cave',
  'notes',
  'diagnosis',
  'pat_number',
  'locale',
] as const;

export type PatientFieldKey = (typeof PATIENT_FIELD_KEYS)[number];

export const DEFAULT_PATIENT_FIELDS: readonly PatientFieldKey[] = [
  'name',
  'contact',
  'address',
  'job_title',
  'cave',
  'notes',
  'diagnosis',
];

export type ReportPartFlags = {
  answers: boolean;
  score: boolean;
  charts: boolean;
  comments: boolean;
};

export type PatientFieldsSection = {
  id: string;
  kind: 'patient_fields';
  heading: string;
  fields: PatientFieldKey[];
};

export type JournalSection = {
  id: string;
  kind: 'journal';
  heading: string;
  /**
   * Explicit note ids. An empty list includes nothing.
   * null is an older section: nothing, unless `limit` is set (that saved count).
   */
  noteIds: string[] | null;
  limit: number | null;
  order: 'newest' | 'oldest';
};

export type MarkingsSection = {
  id: string;
  kind: 'markings';
  heading: string;
  tagIds: string[];
  /** Explicit marking ids. Empty includes nothing. Tags do not add markings. */
  markingIds: string[];
  order: 'newest' | 'oldest' | 'file';
  includeQuote: boolean;
  includeComment: boolean;
  includeFileExtract: boolean;
};

export type QuestionnaireSection = {
  id: string;
  kind: 'questionnaire';
  heading: string;
  formsTemplateId: string | null;
  formsTemplateName: string;
  assignmentId: string | null;
  parts: ReportPartFlags;
  /**
   * Which figures are on. null means every figure (older reports, and the master switch).
   * An empty list means none. Ids are figure names such as `radar` or `bar-soevn`, not patient data.
   */
  chartIds: string[] | null;
  allQuestions: boolean;
  questionIds: string[];
};

export type FileSection = {
  id: string;
  kind: 'file';
  heading: string;
  fileIds: string[];
};

export type TextSection = {
  id: string;
  kind: 'text';
  heading: string;
  body: string;
};

/** What stands in front of each topic in the contents list. */
export const TOC_NUMBERINGS = ['numbers', 'letters', 'none'] as const;
export type TocNumbering = (typeof TOC_NUMBERINGS)[number];

/** How a section heading is drawn. Missing means bold, including older saved reports. */
export const HEADING_STYLES = ['bold', 'italic', 'both'] as const;
export type HeadingStyle = (typeof HEADING_STYLES)[number];

export function headingStyleOf(value: unknown): HeadingStyle {
  return value === 'italic' || value === 'both' ? value : 'bold';
}

/** Bold is omitted so older sections stay unchanged. */
function styleFields(
  value: unknown,
): { headingStyle: Exclude<HeadingStyle, 'bold'> } | Record<string, never> {
  if (value === 'italic' || value === 'both') return { headingStyle: value };
  return {};
}

export type TocSection = {
  id: string;
  kind: 'toc';
  heading: string;
  numbering: TocNumbering;
};

/**
 * Points at a clinic text tag. textId picks one saved text when the behandler
 * has several. The wording is read live and is not stored on the report.
 */
export type UserTextSection = {
  id: string;
  kind: 'user_text';
  heading: string;
  typeId: string | null;
  textId?: string | null;
};

export type ReportSection = (
  | PatientFieldsSection
  | JournalSection
  | MarkingsSection
  | QuestionnaireSection
  | FileSection
  | TextSection
  | TocSection
  | UserTextSection
) & {
  /** Start the next section on a new page. */
  pageBreakAfter?: boolean;
  /**
   * Heading in the report, and the same line in the table of contents.
   * Omitted when bold, which is the default for new and older sections.
   */
  headingStyle?: HeadingStyle;
};

export type ReportSectionKind = ReportSection['kind'];

export type ReportPreviewLabels = {
  name: string;
  cpr: string;
  email: string;
  phone: string;
  address: string;
  jobTitle: string;
  cave: string;
  notes: string;
  diagnosis: string;
  patNumber: string;
  locale: string;
  notRecorded: string;
  cprHidden: string;
  cprLoading: string;
  comment: string;
  file: string;
  page: string;
  quote: string;
  score: string;
  answer: string;
  missingQuestionnaire: string;
  /** Shown when a questionnaire section has not named one form yet. */
  chooseQuestionnaire: string;
  /** Shown when a journal section has no chosen notes. */
  chooseJournal: string;
  /** Shown when a markings section has no chosen markings. */
  chooseMarking: string;
  /** Shown when the named form is not on this patient. */
  formMissing: string;
};

export type ReportPreviewChart = ChartFigure;

export type ReportPreviewBlock = {
  id: string;
  heading: string;
  /** Omitted when the heading is bold. */
  headingStyle?: HeadingStyle;
  body: string;
  charts: ReportPreviewChart[];
  pageBreakAfter?: boolean;
  /** Contents lines. Each line uses the heading style of the section it names. */
  tocEntries?: readonly { label: string; style: HeadingStyle }[];
};

export type ReportPreviewModel = {
  letterhead: PatientReportLetterheadMode;
  cover: { title: string; body: string } | null;
  title: string;
  blocks: ReportPreviewBlock[];
};

export type QuestionnaireAnswerPreview = {
  id: string;
  prompt: string;
  answer: string;
  comment: string | null;
};

export type QuestionnairePreviewDetail = {
  answers: QuestionnaireAnswerPreview[];
  scoreText: string | null;
  charts: ReportPreviewChart[];
};

export type CprPreviewAccess = 'hidden' | 'loading' | 'ready';

export type PatientReportTemplate = {
  id: string;
  name: string;
  /** Report name used when this template is chosen. Empty follows `name`. */
  reportTitle: string;
  description: string;
  includeCover: boolean;
  coverTitle: string;
  coverBody: string;
  letterhead: PatientReportLetterheadChoice;
  sections: ReportSection[];
  /** Object key of this template's .docx, or null when the clinic file is used. */
  docxStoragePath: string | null;
  /** Object key of the cover sheet, or null when title and text are the cover. */
  coverStoragePath: string | null;
  coverMime: 'application/pdf' | 'image/png' | 'image/jpeg' | null;
  updatedAt: string;
};

const ID_RE = /^[A-Za-z0-9_-]{1,80}$/;

function record(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function docxPath(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!/^[A-Za-z0-9._/-]{1,220}$/.test(trimmed) || trimmed.includes('..')) return null;
  return trimmed.toLowerCase().endsWith('.docx') ? trimmed : null;
}

function coverPath(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!/^[A-Za-z0-9._/-]{1,220}$/.test(trimmed) || trimmed.includes('..')) return null;
  return /\.(pdf|png|jpg)$/i.test(trimmed) ? trimmed : null;
}

function coverMime(value: unknown): 'application/pdf' | 'image/png' | 'image/jpeg' | null {
  if (value === 'application/pdf' || value === 'image/png' || value === 'image/jpeg') return value;
  return null;
}

function shortId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return ID_RE.test(trimmed) ? trimmed : null;
}

function idList(value: unknown, max = 80): string[] {
  if (!Array.isArray(value)) return [];
  const ids: string[] = [];
  for (const item of value) {
    const id = shortId(item);
    if (!id || ids.includes(id)) continue;
    ids.push(id);
    if (ids.length >= max) break;
  }
  return ids;
}

function headingOf(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return (trimmed || fallback).slice(0, 300);
}

function clip(value: unknown, max: number): string {
  return typeof value === 'string' ? value.slice(0, max) : '';
}

function flag(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function limitOf(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const next = Math.trunc(value);
  if (next < 1) return null;
  return Math.min(next, 50);
}

function fieldList(value: unknown): PatientFieldKey[] {
  if (!Array.isArray(value)) return [];
  const fields: PatientFieldKey[] = [];
  for (const item of value) {
    if (typeof item !== 'string') continue;
    if (!(PATIENT_FIELD_KEYS as readonly string[]).includes(item)) continue;
    const key = item as PatientFieldKey;
    if (fields.includes(key)) continue;
    fields.push(key);
  }
  return fields;
}

function chartIdsOf(value: unknown): string[] | null {
  if (value == null) return null;
  if (!Array.isArray(value)) return null;
  return idList(value, 80);
}

function partsOf(value: unknown): ReportPartFlags {
  const row = record(value);
  return {
    answers: flag(row?.answers, true),
    score: flag(row?.score, true),
    charts: flag(row?.charts, true),
    comments: flag(row?.comments, true),
  };
}

function noteIdsOf(value: unknown): string[] | null {
  if (value == null) return null;
  return idList(value);
}

/** Drop anything that is not a selection or user-written text. */
export function parseReportSections(value: unknown): ReportSection[] {
  const source = Array.isArray(value) ? value : [];
  const sections: ReportSection[] = [];
  for (const item of source) {
    const row = record(item);
    const id = shortId(row?.id);
    const kind = row?.kind;
    if (!row || !id || typeof kind !== 'string') continue;
    const heading = headingOf(row.heading, '');
    const pageBreakAfter = row.pageBreakAfter === true ? { pageBreakAfter: true as const } : {};
    const headingStyle = styleFields(row.headingStyle);
    if (kind === 'patient_fields') {
      sections.push({
        id,
        kind,
        heading,
        fields: fieldList(row.fields),
        ...pageBreakAfter,
        ...headingStyle,
      });
    } else if (kind === 'journal') {
      const order = row.order === 'oldest' ? 'oldest' : 'newest';
      sections.push({
        id,
        kind,
        heading,
        noteIds: noteIdsOf(row.noteIds),
        limit: limitOf(row.limit),
        order,
        ...pageBreakAfter,
        ...headingStyle,
      });
    } else if (kind === 'markings') {
      const order = row.order === 'oldest' || row.order === 'file' ? row.order : 'newest';
      sections.push({
        id,
        kind,
        heading,
        tagIds: idList(row.tagIds),
        markingIds: idList(row.markingIds),
        order,
        includeQuote: flag(row.includeQuote, true),
        includeComment: flag(row.includeComment, true),
        includeFileExtract: flag(row.includeFileExtract, true),
        ...pageBreakAfter,
        ...headingStyle,
      });
    } else if (kind === 'questionnaire') {
      sections.push({
        id,
        kind,
        heading,
        formsTemplateId: shortId(row.formsTemplateId),
        formsTemplateName: clip(row.formsTemplateName, 200).trim(),
        assignmentId: shortId(row.assignmentId),
        parts: partsOf(row.parts),
        chartIds: chartIdsOf(row.chartIds),
        allQuestions: flag(row.allQuestions, true),
        questionIds: idList(row.questionIds, 200),
        ...pageBreakAfter,
        ...headingStyle,
      });
    } else if (kind === 'file') {
      sections.push({
        id,
        kind,
        heading,
        fileIds: idList(row.fileIds),
        ...pageBreakAfter,
        ...headingStyle,
      });
    } else if (kind === 'text') {
      sections.push({
        id,
        kind,
        heading,
        body: clip(row.body, 20000),
        ...pageBreakAfter,
        ...headingStyle,
      });
    } else if (kind === 'toc') {
      sections.push({
        id,
        kind,
        heading,
        numbering: tocNumberingOf(row.numbering),
        ...pageBreakAfter,
        ...headingStyle,
      });
    } else if (kind === 'user_text') {
      const textId = shortId(row.textId);
      sections.push({
        id,
        kind,
        heading,
        typeId: shortId(row.typeId),
        ...(textId ? { textId } : {}),
        ...pageBreakAfter,
        ...headingStyle,
      });
    }
    if (sections.length >= 40) break;
  }
  return sections;
}

export function parsePatientReportTemplate(value: unknown): PatientReportTemplate | null {
  const row = record(value);
  const id = shortId(row?.id);
  const name = typeof row?.name === 'string' ? row.name.trim() : '';
  if (!row || !id || !name) return null;
  const letterheadRaw = row.letterhead;
  const letterhead: PatientReportLetterheadChoice =
    letterheadRaw === 'none' || letterheadRaw === 'invoice' || letterheadRaw === 'custom'
      ? letterheadRaw
      : 'inherit';
  return {
    id,
    name: name.slice(0, 120),
    reportTitle: clip(row.report_title, 200).trim(),
    description: clip(row.description, 500).trim(),
    includeCover: row.include_cover === true,
    coverTitle: clip(row.cover_title, 300).trim(),
    coverBody: clip(row.cover_body, 4000),
    letterhead,
    sections: parseReportSections(row.sections),
    docxStoragePath: docxPath(row.docx_storage_path),
    coverStoragePath: coverPath(row.cover_storage_path),
    coverMime: coverMime(row.cover_mime),
    updatedAt: typeof row.updated_at === 'string' ? row.updated_at : '',
  };
}

export function parsePatientReportTemplateList(value: unknown): {
  templates: PatientReportTemplate[];
  tags: HighlightTagSource[];
} {
  const row = record(value);
  const templates = (Array.isArray(row?.templates) ? row.templates : [])
    .map(parsePatientReportTemplate)
    .filter((item): item is PatientReportTemplate => Boolean(item));
  const tags = (Array.isArray(row?.tags) ? row.tags : []).flatMap((item) => {
    const tag = record(item);
    const id = shortId(tag?.id);
    const name = typeof tag?.name === 'string' ? tag.name.trim() : '';
    if (!id || !name) return [];
    return [{ id, name: name.slice(0, 80) }];
  });
  return { templates, tags };
}

export function resolveLetterheadMode(
  choice: PatientReportLetterheadChoice | null | undefined,
  clinicDefault: PatientReportLetterheadMode | null | undefined,
): PatientReportLetterheadMode {
  if (choice && choice !== 'inherit') return choice;
  return clinicDefault === 'invoice' || clinicDefault === 'custom' ? clinicDefault : 'none';
}

export function createReportSection(kind: ReportSectionKind, heading: string): ReportSection {
  const id = newReportBlockId();
  if (kind === 'patient_fields') {
    return { id, kind, heading, fields: [...DEFAULT_PATIENT_FIELDS] };
  }
  if (kind === 'journal') {
    return { id, kind, heading, noteIds: [], limit: null, order: 'newest' };
  }
  if (kind === 'markings') {
    return {
      id,
      kind,
      heading,
      tagIds: [],
      markingIds: [],
      order: 'newest',
      includeQuote: true,
      includeComment: true,
      includeFileExtract: true,
    };
  }
  if (kind === 'questionnaire') {
    return {
      id,
      kind,
      heading,
      formsTemplateId: null,
      formsTemplateName: '',
      assignmentId: null,
      parts: { answers: true, score: true, charts: true, comments: true },
      chartIds: null,
      allQuestions: true,
      questionIds: [],
    };
  }
  if (kind === 'file') return { id, kind, heading, fileIds: [] };
  if (kind === 'toc') return { id, kind, heading, numbering: 'none' };
  if (kind === 'user_text') return { id, kind, heading, typeId: null };
  return { id, kind: 'text', heading, body: '' };
}

/**
 * Keep the same row when the type changes: id, heading, page break and heading style.
 * Choices that belong only to the previous type start over.
 */
export function withSectionKind(
  section: ReportSection,
  kind: ReportSectionKind,
  heading: string,
): ReportSection {
  if (section.kind === kind) {
    return section.heading === heading ? section : { ...section, heading };
  }
  const created = createReportSection(kind, heading);
  return {
    ...created,
    id: section.id,
    ...(section.pageBreakAfter === true ? { pageBreakAfter: true } : {}),
    ...(section.headingStyle === 'italic' || section.headingStyle === 'both'
      ? { headingStyle: section.headingStyle }
      : {}),
  };
}

export function tocNumberingOf(value: unknown): TocNumbering {
  return value === 'numbers' || value === 'letters' ? value : 'none';
}

/** A, B, … Z, AA. The index is the entry's place in this contents list. */
export function tocLetter(index: number): string {
  let n = index;
  let label = '';
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

export function tocMarker(index: number, numbering: TocNumbering): string {
  if (numbering === 'numbers') return `${index + 1}. `;
  if (numbering === 'letters') return `${tocLetter(index)}. `;
  return '';
}

export type TocListEntry = { title: string; label: string; style: HeadingStyle };

/** Other sections' headings, in list order. The contents section itself is not an entry. */
export function tocEntries(
  sections: readonly { kind: ReportSectionKind; headingStyle?: HeadingStyle }[],
  numbering: TocNumbering,
  headings: readonly string[],
): TocListEntry[] {
  const entries: TocListEntry[] = [];
  sections.forEach((section, index) => {
    if (section.kind === 'toc') return;
    const title = headings[index]?.trim() ?? '';
    if (!title) return;
    entries.push({
      title,
      label: `${tocMarker(entries.length, numbering)}${title}`,
      style: headingStyleOf(section.headingStyle),
    });
  });
  return entries;
}

/**
 * Add one saved text. The body is not copied onto the report.
 * The same saved text is added once. Another text with the same tag can still be added.
 */
export function insertUserTextSection(
  sections: readonly ReportSection[],
  typeId: string,
  heading: string,
  textId?: string | null,
): ReportSection[] {
  const id = shortId(typeId);
  const savedId = shortId(textId);
  if (!id) return [...sections];
  if (
    sections.some((section) => {
      if (section.kind !== 'user_text') return false;
      if (savedId) return section.textId === savedId;
      return section.typeId === id && !section.textId;
    })
  ) {
    return sections as ReportSection[];
  }
  const created = createReportSection('user_text', heading);
  if (created.kind !== 'user_text') return [...sections];
  return [
    ...sections,
    {
      ...created,
      typeId: id,
      ...(savedId ? { textId: savedId } : {}),
      heading: heading.slice(0, 300),
    },
  ];
}

export function moveSection<T extends { id: string }>(
  items: readonly T[],
  id: string,
  direction: -1 | 1,
): T[] {
  const index = items.findIndex((item) => item.id === id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= items.length) return [...items];
  const next = [...items];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

export function reorderSections<T extends { id: string }>(
  items: readonly T[],
  activeId: string,
  overId: string,
): T[] {
  const from = items.findIndex((item) => item.id === activeId);
  const to = items.findIndex((item) => item.id === overId);
  if (from < 0 || to < 0 || from === to) return [...items];
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function updateSection(
  sections: readonly ReportSection[],
  id: string,
  patch: ReportSection,
): ReportSection[] {
  return sections.map((section) => (section.id === id ? patch : section));
}

export function removeSection(sections: readonly ReportSection[], id: string): ReportSection[] {
  return sections.filter((section) => section.id !== id);
}

function replaceSection(
  sections: readonly ReportSection[],
  index: number,
  section: ReportSection,
): ReportSection[] {
  return sections.map((item, itemIndex) => (itemIndex === index ? section : item));
}

export function selectedJournalNotes(
  section: JournalSection,
  catalog: PatientReportCatalog,
): JournalNoteSource[] {
  const sorted = [...catalog.journalNotes].sort((a, b) => {
    const cmp = a.entryDate.localeCompare(b.entryDate);
    return section.order === 'oldest' ? cmp : -cmp;
  });
  if (section.noteIds == null) return section.limit ? sorted.slice(0, section.limit) : [];
  const allow = new Set(section.noteIds);
  return sorted.filter((note) => allow.has(note.id));
}

export function toggleJournalNote(
  sections: readonly ReportSection[],
  noteId: string,
  checked: boolean,
  resolvedIds: readonly string[],
  heading: string,
): ReportSection[] {
  const index = sections.findIndex((section) => section.kind === 'journal');
  if (index < 0) {
    if (!checked) return [...sections];
    const created = createReportSection('journal', heading);
    if (created.kind !== 'journal') return [...sections];
    return [...sections, { ...created, noteIds: [noteId] }];
  }
  const section = sections[index];
  if (section.kind !== 'journal') return [...sections];
  const base = section.noteIds ?? [...resolvedIds];
  const nextIds = checked
    ? [...base, noteId].filter((id, idIndex, all) => all.indexOf(id) === idIndex)
    : base.filter((id) => id !== noteId);
  if (nextIds.length === 0) return sections.filter((_, itemIndex) => itemIndex !== index);
  return replaceSection(sections, index, { ...section, noteIds: nextIds, limit: null });
}

export function selectedMarkings(
  section: MarkingsSection,
  catalog: PatientReportCatalog,
): MarkingSource[] {
  const rows = catalog.markings.filter((marking) => section.markingIds.includes(marking.id));
  return rows.sort((a, b) => {
    if (section.order === 'file') {
      return (
        a.filename.localeCompare(b.filename, 'da') ||
        (a.createdAt ?? '').localeCompare(b.createdAt ?? '')
      );
    }
    const cmp = (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
    return section.order === 'oldest' ? cmp : -cmp;
  });
}

export function toggleMarking(
  sections: readonly ReportSection[],
  markingId: string,
  checked: boolean,
  heading: string,
): ReportSection[] {
  const index = sections.findIndex((section) => section.kind === 'markings');
  if (index < 0) {
    if (!checked) return [...sections];
    const created = createReportSection('markings', heading);
    if (created.kind !== 'markings') return [...sections];
    return [...sections, { ...created, markingIds: [markingId] }];
  }
  const section = sections[index];
  if (section.kind !== 'markings') return [...sections];
  const base = section.markingIds;
  const nextIds = checked
    ? [...base, markingId].filter((id, idIndex, all) => all.indexOf(id) === idIndex)
    : base.filter((id) => id !== markingId);
  if (nextIds.length === 0) return sections.filter((_, itemIndex) => itemIndex !== index);
  return replaceSection(sections, index, { ...section, markingIds: nextIds });
}

export function togglePatientFields(
  sections: readonly ReportSection[],
  checked: boolean,
  heading: string,
): ReportSection[] {
  const exists = sections.some((section) => section.kind === 'patient_fields');
  if (checked && !exists) return [...sections, createReportSection('patient_fields', heading)];
  if (!checked && exists) return sections.filter((section) => section.kind !== 'patient_fields');
  return [...sections];
}

export function toggleField(
  fields: readonly PatientFieldKey[],
  key: PatientFieldKey,
  checked: boolean,
): PatientFieldKey[] {
  if (checked) return fields.includes(key) ? [...fields] : [...fields, key];
  return fields.filter((field) => field !== key);
}

export function moveField(
  fields: readonly PatientFieldKey[],
  key: PatientFieldKey,
  direction: -1 | 1,
): PatientFieldKey[] {
  const index = fields.indexOf(key);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= fields.length) return [...fields];
  const next = [...fields];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

export function questionnaireNamesAForm(section: QuestionnaireSection): boolean {
  return Boolean(
    section.formsTemplateId || section.formsTemplateName.trim() || section.assignmentId,
  );
}

/** A named questionnaire stays on the report even when this patient has no copy of it. */
export function questionnaireIsMissing(
  section: QuestionnaireSection,
  catalog: PatientReportCatalog,
): boolean {
  if (!questionnaireNamesAForm(section)) return false;
  return matchedQuestionnaire(section, catalog) == null;
}

export function missingQuestionnaireEntries(
  sections: readonly ReportSection[],
  catalog: PatientReportCatalog,
): { id: string; name: string }[] {
  const rows: { id: string; name: string }[] = [];
  const seen = new Set<string>();
  for (const section of sections) {
    if (section.kind !== 'questionnaire' || !questionnaireIsMissing(section, catalog)) continue;
    const id = section.formsTemplateId || section.assignmentId || section.formsTemplateName;
    const name = section.formsTemplateName.trim() || section.heading.trim() || id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    rows.push({ id, name });
  }
  return rows;
}

export function matchedQuestionnaire(
  section: QuestionnaireSection,
  catalog: PatientReportCatalog,
): QuestionnaireSource | null {
  if (section.assignmentId) {
    return catalog.questionnaires.find((item) => item.id === section.assignmentId) ?? null;
  }
  return (
    catalog.questionnaires.find((item) => {
      if (section.formsTemplateId && item.formsTemplateId === section.formsTemplateId) return true;
      if (section.formsTemplateName && item.name === section.formsTemplateName) return true;
      return false;
    }) ?? null
  );
}

export function questionnaireAssignmentIds(
  sections: readonly ReportSection[],
  catalog: PatientReportCatalog,
): string[] {
  const ids: string[] = [];
  for (const section of sections) {
    if (section.kind !== 'questionnaire') continue;
    const match = matchedQuestionnaire(section, catalog);
    if (match && !ids.includes(match.id)) ids.push(match.id);
  }
  return ids;
}

export function toggleFile(
  sections: readonly ReportSection[],
  fileId: string,
  checked: boolean,
  heading: string,
): ReportSection[] {
  const index = sections.findIndex((section) => section.kind === 'file');
  if (index < 0) {
    if (!checked) return [...sections];
    const created = createReportSection('file', heading);
    if (created.kind !== 'file') return [...sections];
    return [...sections, { ...created, fileIds: [fileId] }];
  }
  const section = sections[index];
  if (section.kind !== 'file') return [...sections];
  const fileIds = checked
    ? [...section.fileIds, fileId].filter((id, idIndex, all) => all.indexOf(id) === idIndex)
    : section.fileIds.filter((id) => id !== fileId);
  if (fileIds.length === 0) return sections.filter((_, itemIndex) => itemIndex !== index);
  return replaceSection(sections, index, { ...section, fileIds });
}

export function upsertQuestionnaireAssignment(
  sections: readonly ReportSection[],
  form: QuestionnaireSource,
  checked: boolean,
  heading: string,
): ReportSection[] {
  const index = sections.findIndex(
    (section) =>
      section.kind === 'questionnaire' &&
      (section.assignmentId === form.id ||
        (section.assignmentId == null &&
          ((section.formsTemplateId != null && section.formsTemplateId === form.formsTemplateId) ||
            (section.formsTemplateName !== '' && section.formsTemplateName === form.name)))),
  );
  if (!checked) {
    if (index < 0) return [...sections];
    return sections.filter((_, itemIndex) => itemIndex !== index);
  }
  if (index < 0) {
    const created = createReportSection('questionnaire', heading);
    if (created.kind !== 'questionnaire') return [...sections];
    return [
      ...sections,
      {
        ...created,
        heading: created.heading || form.name,
        formsTemplateId: form.formsTemplateId,
        formsTemplateName: form.name,
        assignmentId: form.id,
      },
    ];
  }
  const section = sections[index];
  if (section.kind !== 'questionnaire') return [...sections];
  return replaceSection(sections, index, {
    ...section,
    formsTemplateId: form.formsTemplateId ?? section.formsTemplateId,
    formsTemplateName: form.name || section.formsTemplateName,
    assignmentId: form.id,
  });
}

function line(
  label: string,
  value: string | null,
  showEmpty: boolean,
  empty: string,
): string | null {
  if (value && value.trim()) return `${label}: ${value.trim()}`;
  if (!showEmpty) return null;
  return `${label}: ${empty}`;
}

function patientFieldBody(
  section: PatientFieldsSection,
  patient: PatientCardSource | null,
  cprAccess: CprPreviewAccess,
  cpr: string | null,
  labels: ReportPreviewLabels,
  showEmpty: boolean,
): string {
  const lines: string[] = [];
  for (const field of section.fields) {
    if (field === 'cpr') {
      if (cprAccess === 'hidden') {
        if (showEmpty) lines.push(`${labels.cpr}: ${labels.cprHidden}`);
        continue;
      }
      if (cprAccess === 'loading') {
        if (showEmpty) lines.push(`${labels.cpr}: ${labels.cprLoading}`);
        continue;
      }
      const formatted = cpr ? formatRevealedCpr(cpr) : null;
      const row = line(labels.cpr, formatted, showEmpty, labels.notRecorded);
      if (row) lines.push(row);
      continue;
    }
    if (field === 'contact') {
      const email = line(labels.email, patient?.email ?? null, showEmpty, labels.notRecorded);
      const phone = line(labels.phone, patient?.phone ?? null, showEmpty, labels.notRecorded);
      if (email) lines.push(email);
      if (phone) lines.push(phone);
      continue;
    }
    const value =
      field === 'name'
        ? patient?.name || null
        : field === 'address'
          ? (patient?.address ?? null)
          : field === 'job_title'
            ? (patient?.jobTitle ?? null)
            : field === 'cave'
              ? (patient?.cave ?? null)
              : field === 'notes'
                ? (patient?.notes ?? null)
                : field === 'diagnosis'
                  ? (patient?.diagnosis ?? null)
                  : field === 'pat_number'
                    ? (patient?.patNumber ?? null)
                    : (patient?.preferredLocale ?? null);
    const label =
      field === 'name'
        ? labels.name
        : field === 'address'
          ? labels.address
          : field === 'job_title'
            ? labels.jobTitle
            : field === 'cave'
              ? labels.cave
              : field === 'notes'
                ? labels.notes
                : field === 'diagnosis'
                  ? labels.diagnosis
                  : field === 'pat_number'
                    ? labels.patNumber
                    : labels.locale;
    const row = line(label, value, showEmpty, labels.notRecorded);
    if (row) lines.push(row);
  }
  return lines.join('\n');
}

function markingBody(
  section: MarkingsSection,
  marking: MarkingSource,
  labels: ReportPreviewLabels,
): string {
  const lines: string[] = [];
  if (marking.filename) lines.push(`${labels.file}: ${marking.filename}`);
  if (marking.page != null) lines.push(`${labels.page}: ${marking.page}`);
  const extract = [marking.prefix, marking.quote, marking.suffix]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
  if (section.includeFileExtract && extract) lines.push(extract);
  else if (section.includeQuote && marking.quote) lines.push(`${labels.quote}: ${marking.quote}`);
  if (section.includeComment && marking.comment)
    lines.push(`${labels.comment}: ${marking.comment}`);
  return lines.join('\n');
}

function questionsOf(
  detail: QuestionnairePreviewDetail,
  section: QuestionnaireSection,
): QuestionnaireAnswerPreview[] {
  if (section.allQuestions) return detail.answers;
  const allow = new Set(section.questionIds);
  return detail.answers.filter((answer) => allow.has(answer.id));
}

function questionnaireBody(
  section: QuestionnaireSection,
  detail: QuestionnairePreviewDetail | null,
  labels: ReportPreviewLabels,
): { body: string; charts: ReportPreviewChart[] } {
  if (!detail) {
    return {
      body: section.parts.answers || section.parts.score ? labels.missingQuestionnaire : '',
      charts: [],
    };
  }
  const lines: string[] = [];
  if (section.parts.score && detail.scoreText) lines.push(`${labels.score}: ${detail.scoreText}`);
  const answers = questionsOf(detail, section);
  if (section.parts.answers) {
    for (const answer of answers) lines.push(`${answer.prompt}\n${answer.answer}`);
  }
  if (section.parts.comments) {
    for (const answer of answers) {
      if (answer.comment) lines.push(`${answer.prompt}\n${labels.comment}: ${answer.comment}`);
    }
  }
  return { body: lines.join('\n\n'), charts: detail.charts };
}

/** Figures for this section before the on/off switches. Includes the trend across replies. */
export function questionnaireSectionFigures(input: {
  section: QuestionnaireSection;
  detail: QuestionnairePreviewDetail | null;
  catalog: PatientReportCatalog;
  language: string;
}): ReportPreviewChart[] {
  const match = matchedQuestionnaire(input.section, input.catalog);
  const history = historyChartForTemplate({
    catalog: input.catalog,
    formsTemplateId: input.section.formsTemplateId ?? match?.formsTemplateId ?? null,
    templateName: input.section.formsTemplateName || match?.name || '',
    current:
      match != null
        ? { id: match.id, scoreText: input.detail?.scoreText ?? match.scoreText }
        : null,
    language: input.language,
  });
  return withHistoryChart(input.detail?.charts ?? [], history);
}

function userTextBody(
  section: UserTextSection,
  input: {
    showEmpty: boolean;
    userTexts?: ReadonlyMap<string, string>;
    userTextById?: ReadonlyMap<string, string>;
    manyUserTextTypeIds?: ReadonlySet<string>;
    fillText?: (body: string) => string;
    missingUserText?: string;
    chooseUserText?: string;
    pickUserText?: string;
    blankUserText?: boolean;
  },
): string {
  if (input.blankUserText) return '';
  const fill = (template: string) => (input.fillText ? input.fillText(template) : template);
  if (section.textId) {
    const template = input.userTextById?.get(section.textId);
    if (template == null) return input.showEmpty ? (input.missingUserText ?? '') : '';
    return fill(template);
  }
  if (!section.typeId) return input.showEmpty ? (input.chooseUserText ?? '') : '';
  if (input.manyUserTextTypeIds?.has(section.typeId)) {
    return input.showEmpty ? (input.pickUserText ?? input.chooseUserText ?? '') : '';
  }
  const template = input.userTexts?.get(section.typeId);
  if (template == null) return input.showEmpty ? (input.missingUserText ?? '') : '';
  return fill(template);
}

export function resolveReportPreview(input: {
  title: string;
  includeCover: boolean;
  coverTitle: string;
  coverBody: string;
  letterhead: PatientReportLetterheadChoice | null;
  clinicLetterhead: PatientReportLetterheadMode | null | undefined;
  sections: readonly ReportSection[];
  catalog: PatientReportCatalog;
  cprAccess: CprPreviewAccess;
  cpr: string | null;
  /** da or en. Figure names follow this. Defaults to Danish. */
  language?: string;
  details: Readonly<Record<string, QuestionnairePreviewDetail>>;
  fallbackDetail?: QuestionnairePreviewDetail | null;
  labels: ReportPreviewLabels;
  showEmpty: boolean;
  /** type id -> the signed-in user's only text of that type. Not patient data. */
  userTexts?: ReadonlyMap<string, string>;
  /** saved text id -> body, when a section names one text. */
  userTextById?: ReadonlyMap<string, string>;
  /** Types with more than one saved text. A section must name which one. */
  manyUserTextTypeIds?: ReadonlySet<string>;
  /** Fills [placeholder] tokens. Omit to show the authored template unchanged. */
  fillText?: (body: string) => string;
  /** Shown when the type has several texts and none is chosen yet. */
  pickUserText?: string;
  /** Shown in the editor when this user has not written the type yet. */
  missingUserText?: string;
  /** Shown in the editor when the section has not chosen a type. */
  chooseUserText?: string;
  /** Leave personal-text sections blank. Used while building placeholder values. */
  blankUserText?: boolean;
}): ReportPreviewModel {
  const blocks: ReportPreviewBlock[] = [];
  for (const section of input.sections) {
    if (section.kind === 'patient_fields') {
      blocks.push({
        id: section.id,
        heading: section.heading,
        body: patientFieldBody(
          section,
          input.catalog.patient,
          input.cprAccess,
          input.cpr,
          input.labels,
          input.showEmpty,
        ),
        charts: [],
        pageBreakAfter: section.pageBreakAfter === true,
      });
      continue;
    }
    if (section.kind === 'journal') {
      const notes = selectedJournalNotes(section, input.catalog);
      blocks.push({
        id: section.id,
        heading: section.heading,
        body: notes.length
          ? notes
              .map((note) => {
                const title = note.title ?? '';
                const head = [note.entryDate, title].filter(Boolean).join(' — ');
                return [head, note.body].filter(Boolean).join('\n');
              })
              .join('\n\n')
          : input.showEmpty
            ? input.labels.chooseJournal
            : '',
        charts: [],
        pageBreakAfter: section.pageBreakAfter === true,
      });
      continue;
    }
    if (section.kind === 'markings') {
      const markings = selectedMarkings(section, input.catalog);
      blocks.push({
        id: section.id,
        heading: section.heading,
        body: markings.length
          ? markings.map((marking) => markingBody(section, marking, input.labels)).join('\n\n')
          : input.showEmpty
            ? input.labels.chooseMarking
            : '',
        charts: [],
        pageBreakAfter: section.pageBreakAfter === true,
      });
      continue;
    }
    if (section.kind === 'questionnaire') {
      const named = questionnaireNamesAForm(section);
      const match = matchedQuestionnaire(section, input.catalog);
      if (!input.fallbackDetail && named && !match) {
        blocks.push({
          id: section.id,
          heading: section.heading || section.formsTemplateName,
          body: input.labels.formMissing,
          charts: [],
          pageBreakAfter: section.pageBreakAfter === true,
        });
        continue;
      }
      if (!input.fallbackDetail && !named) {
        blocks.push({
          id: section.id,
          heading: section.heading,
          body: input.labels.chooseQuestionnaire,
          charts: [],
          pageBreakAfter: section.pageBreakAfter === true,
        });
        continue;
      }
      const detail = (match && input.details[match.id]) || input.fallbackDetail || null;
      const rendered = questionnaireBody(section, detail, input.labels);
      const figures = questionnaireSectionFigures({
        section,
        detail,
        catalog: input.catalog,
        language: input.language ?? 'da',
      });
      blocks.push({
        id: section.id,
        heading: section.heading || match?.name || section.formsTemplateName,
        body: rendered.body,
        charts: selectQuestionnaireCharts(figures, section),
        pageBreakAfter: section.pageBreakAfter === true,
      });
      continue;
    }
    if (section.kind === 'file') {
      const files = input.catalog.files.filter((file) => section.fileIds.includes(file.id));
      blocks.push({
        id: section.id,
        heading: section.heading,
        body: files
          .map((file) => [file.filename, file.mimeType, file.notes].filter(Boolean).join('\n'))
          .join('\n\n'),
        charts: [],
        pageBreakAfter: section.pageBreakAfter === true,
      });
      continue;
    }
    if (section.kind === 'toc') {
      blocks.push({
        id: section.id,
        heading: section.heading,
        body: '',
        charts: [],
        pageBreakAfter: section.pageBreakAfter === true,
      });
      continue;
    }
    if (section.kind === 'user_text') {
      blocks.push({
        id: section.id,
        heading: section.heading,
        body: userTextBody(section, input),
        charts: [],
        pageBreakAfter: section.pageBreakAfter === true,
      });
      continue;
    }
    blocks.push({
      id: section.id,
      heading: section.heading,
      body: section.body,
      charts: [],
      pageBreakAfter: section.pageBreakAfter === true,
    });
  }
  for (let index = 0; index < input.sections.length; index += 1) {
    const section = input.sections[index];
    const block = blocks[index];
    if (!section || !block) continue;
    if (section.headingStyle === 'italic' || section.headingStyle === 'both') {
      block.headingStyle = section.headingStyle;
    }
    if (section.kind !== 'toc') continue;
    const entries = tocEntries(
      input.sections,
      section.numbering,
      blocks.map((item) => item.heading),
    );
    block.body = entries.map((entry) => entry.label).join('\n');
    block.tocEntries = entries.map((entry) => ({ label: entry.label, style: entry.style }));
  }
  return {
    letterhead: resolveLetterheadMode(input.letterhead, input.clinicLetterhead),
    cover: input.includeCover ? { title: input.coverTitle.trim(), body: input.coverBody } : null,
    title: input.title,
    blocks,
  };
}

export function previewToPdfBlocks(model: ReportPreviewModel): {
  heading: string;
  body: string;
  pageBreakAfter: boolean;
  headingStyle?: HeadingStyle;
  tocLines?: readonly { label: string; style: HeadingStyle }[];
  /** Drawn as bars. Kept off `body` so the PDF paints the figure, not only a number. */
  charts: ReportPreviewChart[];
}[] {
  return model.blocks.flatMap((block) => {
    const body = block.body.trim();
    const charts = block.charts.filter(chartHasFigure);
    const tocLines = block.tocEntries && block.tocEntries.length > 0 ? block.tocEntries : undefined;
    if (!block.heading.trim() && !body && !tocLines && charts.length === 0) return [];
    return [
      {
        heading: block.heading,
        body,
        pageBreakAfter: block.pageBreakAfter === true,
        charts,
        ...(block.headingStyle ? { headingStyle: block.headingStyle } : {}),
        ...(tocLines ? { tocLines } : {}),
      },
    ];
  });
}

export type ReportSavePayload = {
  templateId: string | null;
  letterhead: PatientReportLetterheadChoice;
  includeCover: boolean;
  coverTitle: string;
  coverBody: string;
  /** Section choices stored on the report. The save RPC reads this as composition. */
  composition: ReportSection[];
  blocks: PatientReportBlock[];
  sources: PatientReportSourceRef[];
};

/** Persist selections and user-written text. Resolved preview text is not included. */
export function reportSaveSelections(input: {
  patientId: string;
  templateId: string | null;
  letterhead: PatientReportLetterheadChoice;
  includeCover: boolean;
  coverTitle: string;
  coverBody: string;
  sections: readonly ReportSection[];
}): ReportSavePayload {
  const sections = parseReportSections(input.sections);
  const letterhead: PatientReportLetterheadChoice =
    input.letterhead === 'none' || input.letterhead === 'invoice' || input.letterhead === 'custom'
      ? input.letterhead
      : 'inherit';
  const blocks: PatientReportBlock[] = [];
  const sources: PatientReportSourceRef[] = [];
  const seen = new Set<string>();
  const pushSource = (kind: PatientReportSourceRef['kind'], sourceId: string) => {
    const key = `${kind}:${sourceId}`;
    if (seen.has(key)) return;
    seen.add(key);
    sources.push({ kind, sourceId, label: '' });
  };
  if (sections.some((section) => section.kind === 'patient_fields') && shortId(input.patientId)) {
    pushSource('patient_card', input.patientId);
  }
  for (const section of sections) {
    if (section.kind === 'text') {
      blocks.push({
        id: section.id,
        kind: 'text',
        sourceId: null,
        heading: section.heading,
        body: section.body,
      });
    } else if (section.kind === 'journal' && section.noteIds) {
      for (const noteId of section.noteIds) pushSource('journal_note', noteId);
    } else if (section.kind === 'markings') {
      for (const markingId of section.markingIds) pushSource('marking', markingId);
    } else if (section.kind === 'questionnaire' && section.assignmentId) {
      pushSource('questionnaire', section.assignmentId);
    } else if (section.kind === 'file') {
      for (const fileId of section.fileIds) pushSource('file', fileId);
    }
  }
  return {
    templateId: shortId(input.templateId),
    letterhead,
    includeCover: input.includeCover,
    coverTitle: input.coverTitle.trim().slice(0, 300),
    coverBody: input.coverBody.slice(0, 4000),
    composition: sections,
    blocks,
    sources,
  };
}

/**
 * Arguments for `save_patient_report`.
 * The section list is `composition`. A caller that still names it `sections`
 * is stored the same way, so a template change is not written as an empty list.
 */
export function savePatientReportRpcArgs(input: {
  clinicId: string;
  patientId: string;
  reportId: string | null;
  title: string;
  blocks: readonly PatientReportBlock[];
  sources: readonly PatientReportSourceRef[];
  templateId?: string | null;
  letterhead?: PatientReportLetterheadChoice;
  includeCover?: boolean;
  coverTitle?: string;
  coverBody?: string;
  composition?: readonly ReportSection[] | null;
  sections?: readonly ReportSection[] | null;
}) {
  const composition = input.composition ?? input.sections;
  return {
    p_clinic_id: input.clinicId,
    p_patient_id: input.patientId,
    p_report_id: input.reportId,
    p_title: input.title,
    p_blocks: input.blocks,
    p_sources: input.sources,
    p_template_id: input.templateId ?? null,
    p_letterhead: input.letterhead ?? 'inherit',
    p_include_cover: input.includeCover ?? false,
    p_cover_title: input.coverTitle ?? '',
    p_cover_body: input.coverBody ?? '',
    p_composition: composition ?? [],
  };
}

export type TemplateSavePayload = {
  name: string;
  reportTitle: string;
  description: string;
  includeCover: boolean;
  coverTitle: string;
  coverBody: string;
  letterhead: PatientReportLetterheadChoice;
  sections: ReportSection[];
};

export function templateReportName(template: {
  name: string;
  reportTitle?: string | null;
}): string {
  const custom = template.reportTitle?.trim() ?? '';
  return custom || template.name.trim();
}

export function templateSavePayload(input: {
  name: string;
  reportTitle?: string;
  description: string;
  includeCover: boolean;
  coverTitle: string;
  coverBody: string;
  letterhead: PatientReportLetterheadChoice;
  sections: readonly ReportSection[];
}): TemplateSavePayload {
  const letterhead: PatientReportLetterheadChoice =
    input.letterhead === 'none' || input.letterhead === 'invoice' || input.letterhead === 'custom'
      ? input.letterhead
      : 'inherit';
  return {
    name: input.name.trim().slice(0, 120),
    reportTitle:
      (input.reportTitle ?? '').trim() && (input.reportTitle ?? '').trim() !== input.name.trim()
        ? (input.reportTitle ?? '').trim().slice(0, 200)
        : '',
    description: input.description.trim().slice(0, 500),
    includeCover: input.includeCover,
    coverTitle: input.coverTitle.trim().slice(0, 300),
    coverBody: input.coverBody.slice(0, 4000),
    letterhead,
    sections: parseReportSections(input.sections),
  };
}

export function sectionsFromStored(
  composition: unknown,
  blocks: readonly PatientReportBlock[],
): ReportSection[] {
  const parsed = parseReportSections(composition);
  if (parsed.length > 0) return parsed;
  return blocks.flatMap((block): ReportSection[] => {
    if (block.kind === 'text') {
      return [{ id: block.id, kind: 'text', heading: block.heading, body: block.body }];
    }
    if (!block.sourceId || !shortId(block.sourceId) || !shortId(block.id)) return [];
    if (block.kind === 'journal_note') {
      return [
        {
          id: block.id,
          kind: 'journal',
          heading: 'Journal',
          noteIds: [block.sourceId],
          limit: null,
          order: 'newest',
        },
      ];
    }
    if (block.kind === 'marking') {
      return [
        {
          id: block.id,
          kind: 'markings',
          heading: 'Markeringer',
          tagIds: [],
          markingIds: [block.sourceId],
          order: 'newest',
          includeQuote: true,
          includeComment: true,
          includeFileExtract: true,
        },
      ];
    }
    if (block.kind === 'questionnaire') {
      return [
        {
          id: block.id,
          kind: 'questionnaire',
          heading: 'Spørgeskema',
          formsTemplateId: null,
          formsTemplateName: '',
          assignmentId: block.sourceId,
          parts: { answers: true, score: true, charts: true, comments: true },
          chartIds: null,
          allQuestions: true,
          questionIds: [],
        },
      ];
    }
    if (block.kind === 'patient_card') {
      return [
        {
          id: block.id,
          kind: 'patient_fields',
          heading: 'Patientoplysninger',
          fields: [...DEFAULT_PATIENT_FIELDS],
        },
      ];
    }
    if (block.kind === 'file') {
      return [{ id: block.id, kind: 'file', heading: 'Filer', fileIds: [block.sourceId] }];
    }
    return [];
  });
}

function withoutComment(response: unknown): unknown {
  const topic = topicRowParts(response);
  if (!topic || typeof response !== 'object' || response == null || Array.isArray(response)) {
    return response;
  }
  const copy = { ...(response as Record<string, unknown>) };
  delete copy.comment;
  delete copy.note;
  return copy;
}

export function questionnairePreviewFromDetail(input: {
  questions: readonly FormsQuestion[];
  responses: Record<string, unknown> | null | undefined;
  scoreDetails: unknown;
  scoringConfig: ScoringConfig | null | undefined;
  templateName: string;
  language: string;
}): QuestionnairePreviewDetail {
  const questions = input.questions ?? [];
  const responses = input.responses ?? {};
  const shown = shownQuestionnaireScore({
    storedScore: input.scoreDetails,
    templateName: input.templateName,
    questions,
    responses,
    scoringConfig: input.scoringConfig,
  });
  let scoreText: string | null = null;
  if (shown.kind === 'total') {
    scoreText = shown.max != null ? `${shown.value}/${shown.max}` : String(shown.value);
  } else if (shown.kind === 'physical_mental') {
    scoreText = `${shown.physical} / ${shown.mental}`;
  }
  const charts = questionnaireCharts({
    questions,
    responses,
    scoreDetails: input.scoreDetails,
    scoringConfig: input.scoringConfig,
    templateName: input.templateName,
    language: input.language,
    shown,
  });
  const answers = questions.map((question) => {
    const response = responses[question.id];
    const topic = topicRowParts(response);
    const prompt = question.text_da?.trim() || question.text;
    return {
      id: question.id,
      prompt,
      answer: formatResponse(question, withoutComment(response), input.language),
      comment: topic?.comment.trim() ? topic.comment.trim() : null,
    };
  });
  return { answers, scoreText, charts };
}

export const EXAMPLE_PATIENT_NAME = 'Eksempel Navnesen';
export const EXAMPLE_DIAGNOSIS = 'Eksempeldiagnose K01';
export const EXAMPLE_MARKING_QUOTE = 'Eksempelcitat fra filen';
export const EXAMPLE_CPR = '0101700000';

export const EXAMPLE_REPORT_CATALOG: PatientReportCatalog = {
  patient: {
    id: 'example-patient',
    patNumber: '1042',
    name: EXAMPLE_PATIENT_NAME,
    jobTitle: 'Lærer',
    email: 'eksempel@klinik.dk',
    phone: '12121212',
    address: 'Eksempelvej 1, 8000 Aarhus',
    notes: 'Eksempelnote om transport',
    cave: 'Penicillin',
    diagnosis: EXAMPLE_DIAGNOSIS,
    preferredLocale: 'da-DK',
  },
  journalNotes: [
    {
      id: 'example-note',
      entryDate: '2026-11-03',
      title: 'Samtale',
      body: 'Eksempelnotat fra journalen.',
    },
  ],
  markings: [
    {
      id: 'example-marking',
      quote: EXAMPLE_MARKING_QUOTE,
      comment: 'Eksempelkommentar',
      filename: 'eksempel.pdf',
      createdAt: '2026-11-03T10:00:00.000Z',
      prefix: 'Før',
      suffix: 'efter',
      page: 2,
      tagIds: ['tag-diagnose'],
    },
    {
      id: 'example-marking-other',
      quote: 'Andet citat',
      comment: null,
      filename: 'andet.pdf',
      createdAt: '2026-11-01T10:00:00.000Z',
      prefix: null,
      suffix: null,
      page: 1,
      tagIds: ['tag-medicin'],
    },
  ],
  questionnaires: [
    {
      id: 'example-assignment',
      formsAssignmentId: null,
      formsTemplateId: 'example-form',
      name: 'Eksempelskema',
      status: 'completed',
      completedAt: '2026-11-02T10:00:00.000Z',
      scoreText: '4',
    },
  ],
  files: [],
  highlightTags: [
    { id: 'tag-diagnose', name: 'Diagnose' },
    { id: 'tag-medicin', name: 'Medicin' },
  ],
};

export function questionsFromUnknown(value: unknown): { id: string; text: string }[] {
  const root = record(value);
  const lists = [
    root?.questions,
    record(root?.questionnaire)?.questions,
    record(root?.json_schema)?.questions,
    record(root?.json_schema_preview)?.questions,
  ];
  const questions: { id: string; text: string }[] = [];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      const row = record(item);
      const id = shortId(row?.id);
      if (!row || !id || questions.some((question) => question.id === id)) continue;
      const text =
        (typeof row.text_da === 'string' && row.text_da.trim()) ||
        (typeof row.text === 'string' && row.text.trim()) ||
        (typeof row.label === 'string' && row.label.trim()) ||
        id;
      questions.push({ id, text: text.slice(0, 300) });
      if (questions.length >= 200) return questions;
    }
  }
  return questions;
}

export const EXAMPLE_QUESTIONNAIRE_DETAIL: QuestionnairePreviewDetail = {
  answers: [
    { id: 'sleep', prompt: 'Hvordan har du sovet?', answer: 'Godt', comment: null },
    { id: 'note', prompt: 'Kommentar til forløbet', answer: '—', comment: 'Mere ro om aftenen' },
  ],
  scoreText: '4/27',
  charts: [{ id: 'bar-soevn', kind: 'bar', label: 'Søvn', value: 4, max: 27 }],
};
